/**
 * UnpaidResellerChargesStrip (TMMPDM-0038, tmm-api 0098): a warning strip of the unpaid reseller
 * charges, one "{count} · {amount}" per currency as the server sent them — never summed across
 * currencies (DATA-003 / TBL-010). Hidden when there are none.
 */
import { Alert, Box } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { formatMoney, formatNumber } from '@/format';
import type { ResellerChargesUnpaid } from '@/api/tmm/types';

interface UnpaidResellerChargesStripProps {
  rows: ResellerChargesUnpaid[];
  locale: string;
}

export function UnpaidResellerChargesStrip({ rows, locale }: UnpaidResellerChargesStripProps) {
  const { t } = useCopy();
  if (rows.length === 0) return null;
  const title = t('dashboard.unpaidCharges.title', 'Unpaid reseller charges:');
  return (
    <Alert severity="warning" sx={{ mb: 3 }}>
      <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
        {title}
      </Box>
      <Box
        component="ul"
        aria-label={title}
        sx={{ display: 'inline', m: 0, p: 0, listStyle: 'none' }}
      >
        {rows.map((row, index) => (
          <Box
            component="li"
            key={row.currency}
            sx={{ display: 'inline', fontVariantNumeric: 'tabular-nums' }}
          >
            {index > 0 ? '; ' : null}
            {/* TMMPDM-0044: `count` picks the plural form; `shown` is the formatted number. */}
            {t('dashboard.unpaidCharges.item', '{{shown}} · {{amount}}', {
              count: row.count,
              shown: formatNumber(row.count, locale),
              amount: formatMoney(row.amountMinor, row.currency, locale),
            })}
          </Box>
        ))}
      </Box>
    </Alert>
  );
}
